<template>
  <div class="wrap">
    <div class="container-fluid">
      <div class="row">
        <div class="col-xs-12 col-md-8">
          <Loader :loading="firstStepLoading" :text="$t('Loading checkout information...')">
            <SfSteps :active="currentStep" @change="updateStep($event)">
              <SfStep :name="$t('Items')">
                <Step
                    title="Shopping Cart"
                    :allowNextStep="true"
                    :currentStep="currentStep"
                    @click:next="currentStep++"
                    @click:back="currentStep--"
                  >

                  <ProductsTable :items="order.Items" />
                </Step>
              </SfStep>
              <SfStep :name="$t('Shipping')">
                <Step
                  title="Shipping Information"
                  :allowNextStep="
                    (pickUpInStore && !!shippingAddress.address) ||
                      (!!shippingMethod.name && !!shippingAddress.address)
                  "
                  :currentStep="currentStep"
                  @click:next="currentStep++"
                  @click:back="currentStep--"
                >
                  <template v-if="allowPickUpInStore || !pickUpInStoreOnly">
                    <SfCheckbox
                      v-model="pickUpInStore"
                      name="Pickup in store"
                      :label="$t('Pickup in store')"
                      @change="resetShippingAddress"
                    />
                  </template>
                  <template v-if="!pickUpInStore && !pickUpInStoreOnly">
                    <SfHeading :title="$t('Shipping Address')" :level="3" />
                    <CheckoutAddress
                      :newAddressPreselected="checkoutShippingAddress.NewAddressPreselected"
                      :addresses="checkoutShippingAddress.ExistingAddresses"
                      :value="shippingAddress"
                      @input="updateStepWithAddress('shippingAddress', $event)"
                    />
                  </template>
                  <template v-if="pickUpInStore || pickUpInStoreOnly">
                    <SfHeading :title="$t('Pickup points')" :level="3" />
                    <PickupPoints
                      :pickupPoints="checkoutShippingAddress.PickupPoints"
                      :value="shippingAddress"
                      @input="updateStepWithAddress('shippingAddress', $event)"
                    />
                  </template>
                  <template v-if="!pickUpInStore && !!shippingAddress.address">
                    <SfHeading :title="$t('Shipping Methods')" :level="3" />
                    <ShippingMethods
                      :shippingMethods="checkoutShippingMethods"
                      v-model="shippingMethod"
                      @input="handleShippingMethodChange"
                    />
                  </template>
                </Step>
              </SfStep>
              <SfStep :name="$t('Payment')">
                <Step
                  title="Payment Information"
                  :allowNextStep="!!paymentMethod.name && !!billingAddress.address"
                  :currentStep="currentStep"
                  @click:next="currentStep++"
                  @click:back="currentStep--"
                >
                  <template>
                    <SfHeading :title="$t('Billing Address')" :level="3" />
                    <SfCheckbox
                      v-if="showSameAsShipping"
                      v-model="sameAsShipping"
                      name="SameAsShipping"
                      :label="$t('Use address from Shipping')"
                      @change="setSameAsShipping"
                    />
                    <CheckoutAddress
                      v-if="!sameAsShipping"
                      :addresses="checkoutShippingAddress.ExistingAddresses"
                      :value="billingAddress"
                      @input="updateStepWithAddress('billingAddress', $event)"
                    />
                  </template>
                  <template v-if="!!shippingAddress.address">
                    <SfHeading :title="$t('Payment Methods')" :level="3" />
                    <PaymentMethods
                      :paymentMethods="filteredCheckoutPaymentMethods"
                      v-model="paymentMethod"
                      @input="updateTotals"
                    />
                  </template>
                </Step>
              </SfStep>
              <SfStep :name="$t('Confirm')">
                <Step
                  title="Confirm Order"
                  :isFinalStep="true"
                  :currentStep="currentStep"
                  @click:confirm="placeOrder"
                  @click:back="currentStep--"
                  :allowPlaceOrder="!!confirmation.terms"
                >
                  <template>
                    <ConfirmOrder
                      :currentStep="currentStep"
                      :shippingAddress="
                        getAddressById(shippingAddress.address) ||
                          getPickupPointAddressById(shippingAddress.address)
                      "
                      :billingAddress="getAddressById(billingAddress.address)"
                      :shippingMethod="shippingMethod.name"
                      :paymentMethod="paymentMethod.name"
                      :cart="cart"
                      :value="confirmation"
                      @input="updateField('confirmation', $event)"
                    />
                  </template>
                </Step>
              </SfStep>
            </SfSteps>
          </Loader>
        </div>
        <div class="col-xs-12 col-md-4">
          <Loader :loading="firstStepLoading" :text="$t('Loading review information...')">
            <OrderReview
              :currentStep="currentStep"
              :shippingAddress="
                getAddressById(shippingAddress.address) ||
                  getPickupPointAddressById(shippingAddress.address)
              "
              :billingAddress="getAddressById(billingAddress.address)"
              :shippingMethod="shippingMethod.name"
              :paymentMethod="paymentMethod.name"
            />
          </Loader>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { SfSteps, SfHeading, SfCheckbox } from '@storefront-ui/vue';
import _ from 'lodash';
import { Loader } from '@/modules/core';
import { user } from '@/modules/user';
import checkout from '../mixins/checkout';
import cart from '../mixins/cart';
import Step from '../components/checkout/Step.vue';
import CheckoutAddress from '../components/checkout/step-parts/CheckoutAddress.vue';
import PickupPoints from '../components/checkout/step-parts/PickupPoints.vue';
import ShippingMethods from '../components/checkout/step-parts/ShippingMethods.vue';
import PaymentMethods from '../components/checkout/step-parts/PaymentMethods.vue';
import ConfirmOrder from '../components/checkout/step-parts/ConfirmOrder.vue';
import ProductsTable from '../components/checkout/step-parts/ProductsTable.vue';
import OrderReview from '../components/checkout/OrderReview.vue';

export default {
  name: 'Checkout',
  mixins: [user, checkout, cart],
  components: {
    SfSteps,
    Step,
    SfHeading,
    SfCheckbox,
    CheckoutAddress,
    PickupPoints,
    ShippingMethods,
    PaymentMethods,
    ConfirmOrder,
    ProductsTable,
    OrderReview,
    Loader,
  },
  created() {
    if (!this.cart.length) {
      this.$router.push('/cart');
    }

    const fullCart = this.fetchCart();
    const currentUser = this.fetchCurrentCustomer();
    Promise.all([fullCart, currentUser])
      .then(() => this.fetchShippingAddress())
      .then((checkoutShippingAddress) => {
        const shippingAddress = _.find(checkoutShippingAddress?.ExistingAddresses || [], {
          Id: this.customer.ShippingAddress,
        })
          || checkoutShippingAddress?.ExistingAddresses[0]
          || {};

        const billingAddress = _.find(this.customer.Addresses || [], {
          Id: this.customer.BillingAddress,
        })
          || this.customer?.Addresses[0]
          || {};

        this.shippingAddress = { address: shippingAddress?.Id };
        this.billingAddress = { address: billingAddress?.Id };
        this.pickUpInStore = checkoutShippingAddress?.PickUpInStore;
        this.allowPickUpInStore = checkoutShippingAddress?.AllowPickUpInStore;
        this.pickUpInStoreOnly = checkoutShippingAddress?.PickUpInStoreOnly;
        this.getShippingMethods(shippingAddress?.Id);
        this.getPaymentMethods(shippingAddress?.CountryId);
      })
      .finally(() => {
        this.firstStepLoading = false;
        this.updateTotals();
      });
  },
  data() {
    return {
      allowPickUpInStore: false,
      pickUpInStore: false,
      pickUpInStoreOnly: false,
      firstStepLoading: true,
      sameAsShipping: false,
      currentStep: 0,
      shippingAddress: {
        address: '',
        id: '',
      },
      shippingMethod: {
        name: '',
        system: '',
      },
      billingAddress: {
        address: '',
      },
      paymentMethod: {
        name: '',
        system: '',
      },
      confirmation: {
        orderNote: '',
        terms: true,
      },
      filteredCheckoutPaymentMethods: {},
    };
  },
  computed: {
    showSameAsShipping() {
      return (
        this.shippingAddress.address
        && !!_.find(
          this.checkoutShippingAddress.ExistingAddresses,
          (address) => address.Id === this.shippingAddress.address,
        )
      );
    },
  },
  watch: {
    shippingMethod(shippingMethod) {
      const { PaymentMethods: paymentMethods } = this.paymentMethods || {};
      if (!paymentMethods) return;

      const filteredCheckoutPaymentMethods = this.pickUpInStore
        ? paymentMethods.filter((payment) => (
            payment?.AvailableShipments && !payment.AvailableShipments.length
        ))
        : this.filterCheckoutPaymentMethods(
          shippingMethod,
          paymentMethods,
        );

      this.filteredCheckoutPaymentMethods = {
        ...this.paymentMethods,
        PaymentMethods: filteredCheckoutPaymentMethods,
      };
    },
  },
  methods: {
    filterCheckoutPaymentMethods(shippingMethod, paymentMethods) {
      return shippingMethod?.name
        ? paymentMethods.filter((item) => {
          const availableShipment = item?.AvailableShipments || [];
          return availableShipment.includes(shippingMethod.name);
        })
        : this.checkoutPaymentMethods;
    },
    placeOrder() {
      this.confirmCheckout(this.mapDateToToalsAndConfirmConfig()).then(
        ({ Success, PlacedOrder }) => {
          if (Success) {
            this.$router.push({
              name: 'Success',
              params: { order: PlacedOrder.OrderNumber },
            });
          }
        },
      );
    },
    resetShippingAddress() {
      this.shippingAddress = {
        address: '',
      };
      this.filteredCheckoutPaymentMethods = this.paymentMethods;
    },
    updateStep(next) {
      if (next < this.currentStep) {
        this.currentStep = next;
      }
    },
    setSameAsShipping(isSameAsShipping) {
      if (isSameAsShipping) {
        this.billingAddress = {
          address: this.shippingAddress?.address,
        };
        this.updateTotals();
      }
    },
    getAddressCountryId(id) {
      const address = this.getAddressById(id) || this.getPickupPointAddressById(id) || {};
      return address?.CountryId;
    },
    getShippingMethods(addressId) {
      if (!addressId) return;
      this.fetchShippingMethods(addressId).then(({ ShippingMethods: shippingMethods }) => {
        const defaultSelected = _.find(shippingMethods, { Selected: true });
        if (!defaultSelected) return;
        this.shippingMethod = {
          name: defaultSelected.Name,
          system: defaultSelected.ShippingRateComputationMethodSystemName,
        };
      });
    },
    getPaymentMethods(countryId) {
      if (!countryId) return;
      this.fetchPaymentMethods(countryId).then((data) => {
        const { PaymentMethods: paymentMethods } = data;
        this.paymentMethods = data;
        const filteredCheckoutPaymentMethods = this.filterCheckoutPaymentMethods(
          this.shippingMethod,
          paymentMethods,
        );
        this.filteredCheckoutPaymentMethods = {
          ...data,
          PaymentMethods: filteredCheckoutPaymentMethods,
        };
        const defaultSelected = _.find(filteredCheckoutPaymentMethods, { Selected: true });
        if (!defaultSelected) return;
        this.paymentMethod = {
          name: defaultSelected.Name,
          system: defaultSelected.PaymentMethodSystemName,
        };
      });
    },
    updateField(fieldName, fieldValue) {
      this[fieldName] = fieldValue;
    },
    mapDateToToalsAndConfirmConfig() {
      return {
        BillingAddressId: this.billingAddress.address,
        ShippingAddressId: !this.pickUpInStore ? this.shippingAddress.address : '',
        ShippingMethodName: this.shippingMethod.name,
        ShippingRateComputationMethodSystemName: this.shippingMethod.system,
        PickUpInStoreId: this.pickUpInStore ? this.shippingAddress.id : '',
        PaymentMethodSystemName: this.paymentMethod.system,
        TermsOfServiceOnOrderConfirmPage: this.confirmation.terms,
        OrderNote: this.confirmation.orderNote,
      };
    },
    handleShippingMethodChange() {
      this.updateTotals();
    },
    updateTotals() {
      this.fetchTotals(this.mapDateToToalsAndConfirmConfig());
    },
    updateStepWithAddress(step, data) {
      const isShipping = step === 'shippingAddress';
      const { newAddress } = data;
      if (newAddress) {
        this.addAddress({
          ...newAddress,
          mainEmail: this.customer.Email,
        })
          .then((resAddress) => {
            this[step] = {
              address: resAddress.data.Id,
            };
            if (isShipping) {
              this.getShippingMethods(resAddress.data.Id);
            }
            this.getPaymentMethods(resAddress.data.CountryId);
          })
          .then(() => {
            this.fetchShippingAddress();
            this.updateTotals();
          });
      } else {
        this[step] = data;
        if (isShipping) {
          const countryId = this.getAddressCountryId(data.address);
          this.getShippingMethods(data.address);
          this.getPaymentMethods(countryId);
          this.updateTotals();
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@storefront-ui/vue/styles';
</style>

<style lang="scss">
@import 'src/scss/_breakpoint.scss';
.checkout {
  .sf-checkbox {
    margin-bottom: var(--spacer-sm);
  }
  .sf-select__options {
    overflow: auto;
  }
}
</style>
